import * as Sentry from '@sentry/react';
import { ProtectedRoute } from 'components/ProtectedRoute/ProtectedRoute';
import { PageLayout } from 'layout/PageLayout';
import { About } from 'pages/AboutUsPage/AboutUsPage';
import { CheckoutPage } from 'pages/CheckoutPage/CheckoutPage';
import { EventPage } from 'pages/EventPage/EventPage';
import { FeatureFlags } from 'pages/FeatureFlags/FeatureFlags';
import { HomePage } from 'pages/HomePage/HomePage';
import { MatchesPage } from 'pages/HomePage/MatchesPage';
import { ConcertsPage } from 'pages/Concerts/ConcertsPage';
import { SingleConcertPage } from 'pages/SingleConcertPage/SingleConcertPage';
import { InternationalEventDetailPage } from 'pages/InternationalEvents/InternationalEventDetailPage/InternationalEventDetailPage';
import { InternationalEventsListingPage } from 'pages/InternationalEvents/InternationalEventsListingPage/InternationalEventsListingPage';
import { InternationalEventsCountryPage } from 'pages/InternationalEvents/InternationalEventsCountryPage/InternationalEventsCountryPage';
import { OrderSummaryPage } from 'pages/OrderSummaryPage/OrderSummaryPage';
import { TermsOfUse } from 'pages/TermsOfUse/TermsOfUse';
import { TicketGeneratorPage } from 'pages/TicketGeneratorPage/TicketGeneratorPage';
import TagManager from 'react-gtm-module';
import { useEffect } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { ORDER_SUMMARY_PATHS, ROUTES } from 'router/routes';
import { FEATURE_FLAGS_ENABLED } from 'utils/constants';
import { isMiniApp } from 'utils/miniAppPlatform';
import TestPage from 'pages/TestPage/TestPage';
import { WorkshopsPage } from 'pages/Workshops/WorkshopsPage';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { useSignInOnce } from 'api/auth/MiniAppAuth';
import { MaintenancePage } from 'pages/MaintenancePage/MaintenancePage';
import { ProfileLayout } from 'layout/ProfileLayout';
import { PersonalInfo } from 'pages/PersonalInfo/PersonalInfo';
import { OrderHistory } from 'pages/History/History';
import { B2bOrganizersLandingPage } from 'pages/B2bOrganizersLandingPage/B2bOrganizersLandingPage';
import { B2bRegistrationPage } from 'pages/B2bRegistrationPage/B2bRegistrationPage';
import { B2bRegistrationSuccessPage } from 'pages/B2bRegistrationPage/B2bRegistrationSuccessPage';
import { InvitationPage } from 'pages/InvitationPage/InvitationPage';
import { Navbar } from 'components/Navbar/Navbar';
import { Flex } from '@chakra-ui/react';
import 'https://cdn.marmot-cloud.com/npm/hylid-bridge/2.10.0/index.js';

Sentry.init({
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  integrations: [
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV6Instrumentation(
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      ),
    }),
    new Sentry.Replay(),
  ],
  tracesSampleRate: import.meta.env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE,
  profilesSampleRate: import.meta.env.VITE_APP_SENTRY_PROFILING_SAMPLE_RATE,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
TagManager.initialize({
  gtmId: 'GTM-WNP4D436',
});

export function App() {
  const isAuthenticated = useSignInOnce();
  const { pathname } = useLocation();

  // Start every new page at the top instead of preserving the previous scroll position.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  if (isMiniApp() && !isAuthenticated) {
    return <LoadingSpinner />;
  }

  return (
    <SentryRoutes>
      <Route
        element={
          <PageLayout withFooter>
            <Outlet />
          </PageLayout>
        }
      >
        <Route path={ROUTES.maintenance} element={<MaintenancePage />} />
        <Route path={ROUTES.home} element={<HomePage />} />
        <Route path={ROUTES.matches} element={<MatchesPage />} />
        <Route path={ROUTES.concerts} element={<ConcertsPage />} />
        <Route path={ROUTES.event} element={<EventPage />} />
        <Route path={ROUTES.singleConcert} element={<SingleConcertPage />} />
        <Route path={ROUTES.internationalEvents} element={<InternationalEventsListingPage />} />
        <Route
          path={ROUTES.internationalEventsCountry}
          element={<InternationalEventsCountryPage />}
        />
        <Route path={ROUTES.internationalEvent} element={<InternationalEventDetailPage />} />
        <Route path={ROUTES.checkout} element={<CheckoutPage />} />
        <Route path={ROUTES.workshops} element={<WorkshopsPage />} />
        <Route path={ROUTES.b2bRegistration} element={<B2bRegistrationPage />} />
        <Route path={ROUTES.b2bRegistrationSuccess} element={<B2bRegistrationSuccessPage />} />
        <Route path={ROUTES.b2bLanding} element={<B2bOrganizersLandingPage />} />
        <Route path={ROUTES.test} element={<TestPage />} />
        {ORDER_SUMMARY_PATHS.map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute>
                <OrderSummaryPage />
              </ProtectedRoute>
            }
          />
        ))}
        <Route
          element={
            <ProfileLayout>
              <Outlet />
            </ProfileLayout>
          }
        >
          <Route
            path={ROUTES.profile}
            element={
              <ProtectedRoute>
                <PersonalInfo />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.history}
            element={
              <ProtectedRoute>
                <OrderHistory />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path={ROUTES.termsOfUse} element={<TermsOfUse />} />
        <Route path={ROUTES.aboutUs} element={<About />} />
        {FEATURE_FLAGS_ENABLED ? (
          <Route path={ROUTES.featureflag} element={<FeatureFlags />} />
        ) : null}
        <Route path={'*'} element={<Navigate replace to={ROUTES.home} />} />
      </Route>
      <Route path={ROUTES.ticketGenerator} element={<TicketGeneratorPage />} />
      <Route
        element={
          <Flex direction='column' minH='100vh'>
            <Navbar />
            <Flex as='main' direction='column' flex={1}>
              <Outlet />
            </Flex>
          </Flex>
        }
      >
        <Route path={ROUTES.invitation} element={<InvitationPage />} />
      </Route>
    </SentryRoutes>
  );
}
